<template>
  <div
    class="c4"
    :class="{ 'c4-up': settings.font.uppercase }"
    :data-form="form"
    :data-traffic="settings.typeTraffic"
    :style="rootStyle"
  >
    <div class="c4-line" aria-hidden="true"></div>
    <div class="c4-sr" role="status" aria-live="assertive">{{ announce }}</div>

    <div class="c4-layout">
      <main class="c4-stage">
        <transition-group
          v-if="visible.length"
          tag="div"
          name="c4"
          class="c4-cards"
          @before-leave="onBeforeLeave"
        >
          <canteen-card
            v-for="tk in visible"
            :key="tk.id"
            :ticket="tk"
            :pos="positions[tk.id]"
            :head="!!head && head.id === tk.id"
            :show-pos="keep > 1"
            :order="settings.order"
            :confirm-auto="confirmAuto"
            :restaurant-id="restaurantId"
            :show-details="settings.infoBox"
            @advance="advance"
            @confirm="onConfirm"
            @retry="retryTicket"
            @signin="signIn"
            @enter-id="focusId()"
            @details="openTicketSheet('details', $event)"
            @items="openTicketSheet('items', $event)"
          />
        </transition-group>
        <canteen-idle v-else />
        <canteen-past
          :tickets="past"
          :limit="keep >= 4 ? 2 : 4"
          :left="clearLeft"
          @open="openTicketSheet('past', $event)"
          @clear="clearAll"
        />
      </main>

      <aside class="c4-dock" :aria-label="tx('id.label')">
        <div class="c4-head">
          <div class="c4-dh">
            <button
              type="button"
              class="c4-pill"
              :class="{ warn: !settings.takeCurrentDate }"
              data-fk="date"
              :aria-label="tx('set.today')"
              @click="openSheet('settings')"
            >
              <i class="mdi" :class="settings.takeCurrentDate ? 'mdi-calendar-today' : 'mdi-calendar-alert'" aria-hidden="true"></i>
              {{ dateLabel }}
            </button>
            <button
              type="button"
              class="c4-ib"
              data-fk="sound"
              :aria-pressed="!settings.sound ? 'true' : 'false'"
              :aria-label="settings.sound ? tx('sound.off') : tx('sound.on')"
              @click="setSetting('sound', !settings.sound)"
            >
              <i class="mdi" :class="settings.sound ? 'mdi-volume-high' : 'mdi-volume-off'" aria-hidden="true"></i>
            </button>
            <button type="button" class="c4-ib" data-fk="menu" :aria-label="tx('menu.title')" @click="openSheet('menu')">
              <i class="mdi mdi-menu" aria-hidden="true"></i>
            </button>
          </div>

          <div v-if="!online || !settings.takeCurrentDate || soundLocked" class="c4-notes">
            <div v-if="!online" class="c4-note bad" role="alert">
              <i class="mdi mdi-wifi-off" aria-hidden="true"></i>
              <div><b>{{ tx("note.offline") }}</b> {{ tx("note.offline_sub") }}</div>
              <button type="button" class="c4-note-a" data-fk="n-net" @click="checkConnection">{{ tx("note.offline_btn") }}</button>
            </div>
            <div v-if="!settings.takeCurrentDate" class="c4-note" role="status">
              <i class="mdi mdi-calendar-alert" aria-hidden="true"></i>
              <div><b>{{ tx("note.date") }} {{ dateLabel }}</b> {{ tx("note.date_sub") }}</div>
              <button type="button" class="c4-note-a" data-fk="n-date" @click="backToToday">{{ tx("note.date_btn") }}</button>
            </div>
            <div v-if="soundLocked" class="c4-note info" role="status">
              <i class="mdi mdi-volume-off" aria-hidden="true"></i>
              <div><b>{{ tx("note.sound") }}</b></div>
              <button type="button" class="c4-note-a" data-fk="n-snd" @click="unlockSound">{{ tx("note.sound_btn") }}</button>
            </div>
          </div>

          <canteen-signal
            :signal="signal"
            :kind="settings.typeTraffic"
            :compact="form === 'phone'"
            :ring-key="cool ? cool.id : 0"
            @next="next"
          />
        </div>

        <template v-if="form !== 'phone'">
          <canteen-keypad
            v-if="pad"
            :can-submit="!!scanInput && ready"
            @key="onIdInput(scanInput + $event)"
            @back="onIdInput(scanInput.slice(0, -1))"
            @submit="submitId"
          />
          <canteen-scanner
            v-else
            :mode="settings.camera"
            :ready="ready"
            :busy="busy"
            :wait-next="waitNext"
            :cool-left="cool ? cool.left : null"
            :cool-total="cool ? cool.total : 0"
            :ring-key="cool ? cool.id : 0"
            @decode="onScanned($event, 'camera')"
            @enable="setSetting('camera', 'on')"
          />
          <canteen-id-field
            ref="idfield"
            :value="scanInput"
            :ready="ready"
            :bad="inputBad"
            :pad="pad"
            :reader-only="settings.camera === 'off'"
            @input="onIdInput"
            @enter="onIdEnter"
            @submit="submitId"
            @clear="onIdInput('')"
            @toggle-pad="pad = !pad"
          />
        </template>
        <canteen-id-field
          v-else
          ref="idfield"
          bar
          :value="scanInput"
          :ready="ready"
          :bad="inputBad"
          @input="onIdInput"
          @enter="onIdEnter"
          @submit="submitId"
          @clear="onIdInput('')"
          @open-camera="openSheet('camera')"
        />
      </aside>
    </div>

    <canteen-sheet :open="sheetOn" :title="sheetTitle" :subtitle="sheetSub" @close="closeSheet">
      <canteen-settings-sheet
        v-if="sheet === 'settings'"
        :settings="settings"
        :scale="scale"
        :presets="presets"
        :languages="languages"
        :confirm-auto="confirmAuto"
        :date="orderDate"
        :wake-supported="wakeSupported"
        @change="onSettingChange"
        @preset="applyPreset"
        @date="onDate"
      />
      <canteen-menu-sheet
        v-else-if="sheet === 'menu'"
        :user="user"
        :company="company"
        :routes="availableRoutes"
        :current="$route.name"
        :fullscreen-supported="fullscreenSupported"
        :fullscreen-on="fullscreenOn"
        @settings="openSheet('settings')"
        @fullscreen="toggleFullscreen"
        @go="goTo"
        @logout="logout"
      />
      <canteen-ticket-sheet
        v-else-if="sheetTicket"
        :mode="sheet"
        :ticket="sheetTicket"
        :restaurant-id="restaurantId"
      />
      <div v-else-if="sheet === 'camera'" class="c4-sheetcam">
        <canteen-scanner
          :mode="settings.camera"
          :ready="ready"
          :busy="busy"
          :wait-next="waitNext"
          :cool-left="cool ? cool.left : null"
          :cool-total="cool ? cool.total : 0"
          :ring-key="cool ? cool.id : 0"
          @decode="onSheetDecode"
          @enable="setSetting('camera', 'on')"
        />
      </div>
      <template v-if="sheet === 'settings'" #footer>
        <span class="c4-save"><i class="mdi mdi-check-circle-outline" aria-hidden="true"></i>{{ tx("set.saved") }}</span>
        <button type="button" class="c4-btn ghost sm" data-fk="reset" @click="resetSettings">{{ tx("set.reset") }}</button>
      </template>
      <template v-else-if="sheet === 'past' && sheetTicket" #footer>
        <span class="c4-save" :class="{ bad: queueFull }">
          <template v-if="queueFull">
            <i class="mdi mdi-alert-circle-outline" aria-hidden="true"></i>{{ tx("past.full") }} ({{ active.length }}/{{ keep }})
          </template>
        </span>
        <button type="button" class="c4-btn ghost sm" data-fk="restore" :disabled="queueFull" @click="restore(sheetTicket.id)">
          <i class="mdi mdi-undo-variant" aria-hidden="true"></i>{{ tx("past.back") }}
        </button>
      </template>
    </canteen-sheet>

    <div class="c4-toast" :class="{ on: toastOn }" role="status">{{ toast }}</div>
  </div>
</template>

<script>
import moment from "moment";
import "@/assets/styles/canteen-v4.css";
import canteenV4Text from "@/mixins/canteenV4Text";
import canteenV4Queue from "@/mixins/canteenV4Queue";
import canteenV4Device from "@/mixins/canteenV4Device";
import { allRoutes } from "@/router";
import { isRouteAvailable } from "@/utils/routeAccess";
import CanteenCard from "@/components/canteen/v4/CanteenCard.vue";
import CanteenIdle from "@/components/canteen/v4/CanteenIdle.vue";
import CanteenPast from "@/components/canteen/v4/CanteenPast.vue";
import CanteenSignal from "@/components/canteen/v4/CanteenSignal.vue";
import CanteenScanner from "@/components/canteen/v4/CanteenScanner.vue";
import CanteenIdField from "@/components/canteen/v4/CanteenIdField.vue";
import CanteenKeypad from "@/components/canteen/v4/CanteenKeypad.vue";
import CanteenSheet from "@/components/canteen/v4/CanteenSheet.vue";
import CanteenSettingsSheet from "@/components/canteen/v4/CanteenSettingsSheet.vue";
import CanteenMenuSheet from "@/components/canteen/v4/CanteenMenuSheet.vue";
import CanteenTicketSheet from "@/components/canteen/v4/CanteenTicketSheet.vue";

const SETTINGS_KEY = "canteen-v4-settings";
const LEGACY_KEY = "naplata-settings"; // podešavanja stare stranice: čitaju se jednom, ništa se ne piše nazad

// veličina prikaza: postavlja tri broja zajedno (slovo korisnika, slovo jela, slika)
const PRESETS = [
  { key: "s", user: 20, food: 20, img: 80 },
  { key: "m", user: 24, food: 24, img: 100 },
  { key: "l", user: 28, food: 28, img: 120 },
  { key: "xl", user: 32, food: 32, img: 140 },
];

const clamp = (n, min, max, fallback) => {
  const v = Number(n);
  return isFinite(v) && v > 0 ? Math.min(max, Math.max(min, v)) : fallback;
};

function defaultSettings() {
  return {
    mode: "auto", // auto | manual
    autoDuration: 6,
    typeTraffic: "one", // none | one | three
    colInRow: 3, // koliko korisnika je na ekranu (1, 2, 3, 4, 6)
    order: "oldest", // glavna kartica: oldest | newest
    clearPreviousOrdersTime: 120,
    takeCurrentDate: true,
    language: "",
    font: { size: { user: 24, food: 24 }, uppercase: false },
    image: { size: 100 },
    sound: true,
    camera: "on", // on | off (samo čitač)
    awake: true,
    infoBox: false,
  };
}

// zapis iz podešavanja (ili stare stranice) → važeća podešavanja
function sanitize(raw, base) {
  const s = base;
  if (!raw || typeof raw !== "object") return s;
  if (["auto", "manual"].includes(raw.mode)) s.mode = raw.mode;
  s.autoDuration = clamp(raw.autoDuration, 1, 60, s.autoDuration);
  if (["none", "one", "three"].includes(raw.typeTraffic)) s.typeTraffic = raw.typeTraffic;
  if ([1, 2, 3, 4, 6].includes(Number(raw.colInRow))) s.colInRow = Number(raw.colInRow);
  if (["oldest", "newest"].includes(raw.order)) s.order = raw.order;
  s.clearPreviousOrdersTime = clamp(raw.clearPreviousOrdersTime, 10, 3600, s.clearPreviousOrdersTime);
  if (typeof raw.takeCurrentDate === "boolean") s.takeCurrentDate = raw.takeCurrentDate;
  if (typeof raw.language === "string" && raw.language) s.language = raw.language;
  if (raw.font && raw.font.size) {
    s.font.size.user = clamp(raw.font.size.user, 12, 72, s.font.size.user);
    s.font.size.food = clamp(raw.font.size.food, 12, 72, s.font.size.food);
  }
  if (raw.font && typeof raw.font.uppercase === "boolean") s.font.uppercase = raw.font.uppercase;
  if (raw.image) s.image.size = clamp(raw.image.size, 40, 240, s.image.size);
  if (typeof raw.sound === "boolean") s.sound = raw.sound;
  if (["on", "off"].includes(raw.camera)) s.camera = raw.camera;
  if (typeof raw.awake === "boolean") s.awake = raw.awake;
  if (typeof raw.infoBox === "boolean") s.infoBox = raw.infoBox;
  return s;
}

export default {
  name: "CanteenV4",
  mixins: [canteenV4Text, canteenV4Queue, canteenV4Device],
  components: {
    CanteenCard,
    CanteenIdle,
    CanteenPast,
    CanteenSignal,
    CanteenScanner,
    CanteenIdField,
    CanteenKeypad,
    CanteenSheet,
    CanteenSettingsSheet,
    CanteenMenuSheet,
    CanteenTicketSheet,
  },
  data() {
    return {
      settings: this.loadSettings(),
      presets: PRESETS,
      pad: false,
      sheet: null, // settings | menu | details | items | past | camera
      sheetOn: false,
      sheetTicketId: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    company() {
      const u = this.user;
      return (u && u.companies && u.companies[0]) || null;
    },
    companySettings() {
      try {
        return JSON.parse(this.company.settings) || {};
      } catch (e) {
        return {};
      }
    },
    restaurantSettings() {
      return (this.company && this.company.restaurant && this.company.restaurant.settings) || {};
    },
    restaurantId() {
      return (this.company && this.company.restaurant && this.company.restaurant.id) || "";
    },
    form() {
      return this.device.isMobile ? "phone" : this.device.isTablet ? "tablet" : "kiosk";
    },
    languages() {
      const list = this.restaurantSettings.language;
      if (!Array.isArray(list)) return [];
      const known = this.$i18n.availableLocales;
      return list
        .map((l) => (typeof l === "string" ? { code: l, name: this.getLanguageText(l) } : { code: l.code, name: l.name || this.getLanguageText(l.code) }))
        .filter((l) => known.includes(l.code));
    },
    scale() {
      const s = this.settings;
      const p = PRESETS.find((x) => x.user === s.font.size.user && x.food === s.font.size.food && x.img === s.image.size);
      return p ? p.key : "";
    },
    rootStyle() {
      const s = this.settings;
      const user = Number(s.font.size.user) || 24;
      const food = Number(s.font.size.food) || 24;
      const img = Number(s.image.size) || 100;
      // isto kao stara stranica: manja slova i slike na tabletu i telefonu
      const m =
        this.form === "phone"
          ? { u: user - 4, f: food - 4, i: img - 30, av: 48 }
          : this.form === "tablet"
          ? { u: Math.max(16, user - 6), f: food - 2, i: img - 10, av: 64 }
          : { u: user, f: food, i: img, av: 76 };
      return {
        "--c4-user": m.u + "px",
        "--c4-food": m.f + "px",
        "--c4-img": Math.max(40, m.i) + "px",
        "--c4-av": m.av + "px",
        "--c4-n": this.form === "phone" ? 1 : this.keep,
        "--sig": this.signalColor,
      };
    },
    dateLabel() {
      return moment(this.orderDate, "YYYY-MM-DD").format("DD.MM.YYYY.");
    },
    sheetTicket() {
      return this.recent.find((t) => t.id === this.sheetTicketId) || null;
    },
    queueFull() {
      return this.active.length >= this.keep;
    },
    sheetTitle() {
      const t = this.sheetTicket;
      switch (this.sheet) {
        case "settings":
          return this.tx("set.title");
        case "menu":
          return this.tx("menu.title");
        case "details":
          return this.tx("det.title");
        case "items":
          return this.tx("items.title");
        case "past":
          return t ? this.ticketName(t) : this.tx("past.title");
        case "camera":
          return this.tx("id.camera");
        default:
          return "";
      }
    },
    sheetSub() {
      const t = this.sheetTicket;
      if (this.sheet === "details") return this.tx("det.sub");
      if (this.sheet === "items" && t) return this.ticketName(t);
      if (this.sheet === "past" && t) {
        return [
          t.kind === "taken" ? "" : `${this.tx("past.scanned")} ${t.at}`,
          t.doneHow === "auto" ? this.tx("past.auto") : `${this.tx("past.manual")} ${t.doneAt}`,
        ]
          .filter(Boolean)
          .join(" · ");
      }
      return "";
    },
    availableRoutes() {
      return allRoutes().filter((route) =>
        isRouteAvailable(route, {
          userType: this.$store.state.user.user && this.$store.state.user.user.type,
          userPermissions: this.$store.state.user.user,
          company: this.$store.state.company,
          restaurant: this.$store.state.restaurant,
        })
      );
    },
  },
  watch: {
    settings: {
      deep: true,
      handler() {
        try {
          localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings));
        } catch (e) {
          // bez čuvanja podešavanja ekran radi dalje
        }
      },
    },
  },
  mounted() {
    const lang = this.settings.language;
    if (lang && this.$i18n.availableLocales.includes(lang)) this.$i18n.locale = lang;
    else this.settings.language = this.$i18n.locale;
  },
  methods: {
    // ---------- podešavanja ----------
    loadSettings() {
      const base = defaultSettings();
      const u = this.$store.state.user.user;
      const company = (u && u.companies && u.companies[0]) || null;
      const timer = company && company.restaurant && company.restaurant.settings && company.restaurant.settings.timer;
      if (timer) base.autoDuration = clamp(timer, 1, 60, base.autoDuration);
      if (company && company.general_type === "complete_meal") base.font.size.food = 32;
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem(SETTINGS_KEY));
      } catch (e) {
        saved = null;
      }
      if (!saved) {
        try {
          const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY));
          if (legacy) saved = legacy; // prvi put: preuzmi podešavanja sa stare stranice
        } catch (e) {
          saved = null;
        }
      }
      return sanitize(saved, base);
    },
    onSettingChange({ path, value }) {
      this.setSetting(path, value);
    },
    setSetting(path, value) {
      const keys = path.split(".");
      const last = keys.pop();
      const target = keys.reduce((o, k) => o[k], this.settings);
      this.$set(target, last, value);
      switch (path) {
        case "mode":
          this.resetPause();
          break;
        case "colInRow":
          this.trim();
          break;
        case "language":
          this.$i18n.locale = value;
          break;
        case "takeCurrentDate":
          if (value) this.orderDate = moment().format("YYYY-MM-DD");
          break;
        case "clearPreviousOrdersTime":
          if (this.recent.length) this.touchClear();
          break;
        case "sound":
          if (value) this.unlockSound();
          break;
        default:
          break;
      }
    },
    applyPreset(key) {
      const p = PRESETS.find((x) => x.key === key);
      if (!p) return;
      this.setSetting("font.size.user", p.user);
      this.setSetting("font.size.food", p.food);
      this.setSetting("image.size", p.img);
    },
    resetSettings() {
      const lang = this.settings.language;
      const fresh = this.loadDefaults();
      Object.keys(fresh).forEach((k) => this.$set(this.settings, k, fresh[k]));
      this.settings.language = lang;
      this.resetPause();
      this.trim();
      this.orderDate = moment().format("YYYY-MM-DD");
      this.say(this.tx("toast.reset"));
    },
    loadDefaults() {
      const base = defaultSettings();
      const timer = this.restaurantSettings.timer;
      if (timer) base.autoDuration = clamp(timer, 1, 60, base.autoDuration);
      if (this.company && this.company.general_type === "complete_meal") base.font.size.food = 32;
      return base;
    },
    onDate(value) {
      if (value) this.orderDate = value;
    },
    backToToday() {
      this.setSetting("takeCurrentDate", true);
      this.say(this.tx("toast.date"));
    },

    // ---------- listovi ----------
    openSheet(name) {
      this.sheet = name;
      this.sheetOn = false;
      this.$nextTick(() => requestAnimationFrame(() => (this.sheetOn = true)));
    },
    openTicketSheet(mode, id) {
      this.sheetTicketId = id;
      this.openSheet(mode);
    },
    closeSheet() {
      if (!this.sheet) return;
      this.sheetOn = false;
      clearTimeout(this._sheetTimer);
      this._sheetTimer = setTimeout(() => {
        this.sheet = null;
        this.sheetTicketId = null;
      }, 300);
    },
    onSheetDecode(raw) {
      this.closeSheet();
      this.onScanned(raw, "camera");
    },
    ticketName(t) {
      const u = t.outcome.user;
      if (u) return `${this.toLatin(u.name)} ${this.toLatin(u.lastname)}`.trim();
      return t.code ? `ID ${t.code}` : this.tx("o." + t.kind);
    },

    // ---------- navigacija ----------
    goTo(name) {
      if (name !== this.$route.name) this.$router.push({ name });
      else this.closeSheet();
    },
    signIn() {
      this.logout();
    },
    // isto kao PaymentDrawer na staroj stranici
    logout() {
      localStorage.removeItem("restoran-token");
      document.cookie = "restoran-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      this.$store.commit("user/LOGOUT", null);
      this.$store.commit("SAVE_RESTAURANT", null);
      this.$store.commit("SAVE_COMPANY", null);
      this.$router.push({ name: "auth-login" });
    },

    // ---------- kartice ----------
    // izlazna kartica se skida iz toka (grid) i bledi na svom mjestu
    onBeforeLeave(el) {
      el.style.top = el.offsetTop + "px";
      el.style.left = el.offsetLeft + "px";
      el.style.width = el.offsetWidth + "px";
    },
  },
};
</script>
